import axios from "axios";

import Vue from "vue";
import VueAxios from "vue-axios";

import i18n from "../i18n/i18n";
import store from "../../store";
import router from "../../router";

import JwtUtilService from "@/services/jwt.util.service";
import AuthService from "@/services/auth.service";
import { APIError, APIResponse } from "./utils";

const CONTENT_TYPE = "application/json; charset=utf-8";
const PUBLIC_URLS = ["refresh-token", "verify-token", "login"];

class APIService {
  constructor() {
    let client = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: parseInt(process.env.VUE_APP_TIMEOUT),
      responseType: "json",
      headers: {
        accept: CONTENT_TYPE,
        "content-type": CONTENT_TYPE,
        client_type: "web",
        "time-zone": this.getTimeZone(),
      },
    });

    client.interceptors.request.use(
      (config) => {
        config.headers["Accept-Language"] = store.getters["enums/lang"];
        this.setAuthHeader(config);

        return config;
      },
      (error) => {
        return Promise.reject(new APIError(error));
      }
    );

    client.interceptors.response.use(
      (response) => {
        return Promise.resolve(new APIResponse(response));
      },
      (error) => {
        window.scrollTo(0, 0);

        if (!error.response) return Promise.reject(new APIError(error));

        if (
          error?.response?.data?.message &&
          error.response.data.message === "Forbidden resource"
        ) {
          // router.push("/permission-denied");
          let temp_error = { ...error };
          temp_error.response.data.message = "";
          return Promise.reject(new APIError(temp_error));
        }

        if (error.response.status === 400 || error.response.status === 409 || error.response.status === 403) {
          let temp_error = { ...error };
          // If the error response is a blob, convert it to text and then to JSON
          if (error.response.data instanceof Blob) { 
            // Convert Blob to Text and Parse as JSON
            return error.response.data.text().then((text) => {
              try {
                const jsonError = JSON.parse(text);
                temp_error.response.data = jsonError;
                // Extract Error Messages If Available:
                if (Array.isArray(jsonError.message)) {
                  temp_error.response.data.message = jsonError.message.map(
                    (each) => each.message[0]
                  );
                }
                // Return the Parsed Error
                return Promise.reject(new APIError(temp_error));
                // Handle Parsing Failure (Invalid JSON)
              } catch (blobError) {
                return Promise.reject(
                  new APIError({
                    ...error,
                    response: {
                      data: { message: "Failed to parse error response" },
                    },
                  })
                );
              }
            });
          }
          if (
            temp_error?.response?.data?.message &&
            Array.isArray(error.response.data.message)
          ) {
            temp_error.response.data.message =
              temp_error.response.data.message?.map((each) => each.message[0]);
          }

          return Promise.reject(new APIError(temp_error));
        }

        if (
          error.response.status !== 401 ||
          error.config.url === "login" ||
          error.config.url === "change-password"
        )
          return Promise.reject(new APIError(error));

        if (error.response.status == 401) {
          // Refresh token call

          const params = new URLSearchParams();
          params.append("grant_type", "refresh_token");
          params.append("refresh_token", JwtUtilService.getRefreshToken());
          params.append("client_id", JwtUtilService.getClientID());

          return AuthService.refreshToken(params)
            .then((res) => {
              const config = error.config;

              JwtUtilService.setToken(res.access_token);
              JwtUtilService.setRefreshToken(res.refresh_token);
              JwtUtilService.setIdToken(res.id_token);

              this.setAuthHeader(config);

              return new Promise((resolve, reject) => {
                client
                  .request(config)
                  .then((response) => {
                    resolve(new APIResponse(response));
                  })
                  .catch((error) => {
                    reject(new APIError(error));
                  });
              });
            })
            .catch((error) => {
              console.debug(error);
              router.push("/session-expired");
              return Promise.reject(new APIError(error));
            });
        }
      }
    );

    Vue.use(VueAxios, client);
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   */
  setAuthHeader(config) {
    let token = JwtUtilService.getToken();

    if (token && !PUBLIC_URLS.includes(config.url))
      config.headers["Authorization"] = `Bearer ${token}`;
  }
  getTimeZone() {
    let offset = new Date().getTimezoneOffset(),
      o = Math.abs(offset);
    return (
      (offset < 0 ? "+" : "-") +
      ("00" + Math.floor(o / 60)).slice(-2) +
      ":" +
      ("00" + (o % 60)).slice(-2)
    );
  }
}

export default APIService;

